import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn, typography } from "@/utilities/tailwind-utils";

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: LucideIcon;
  /**
   * Where the records behind this number live.
   *
   * Optional on purpose. A tile that navigates somewhere only loosely related is worse
   * than one that does not move: it teaches people that clicking is a guess. Leave it
   * unset unless the destination shows the very rows the number counts.
   */
  to?: string;
  /** A signed percentage, rendered as its own line when supplied. */
  change?: number;
  /** Overrides the icon's tint — used where a figure carries a status colour. */
  iconClassName?: string;
  className?: string;
}

function changeTone(change: number): string {
  if (change > 0) return "text-emerald-600 dark:text-emerald-400";
  if (change < 0) return "text-rose-600 dark:text-rose-400";
  return "text-muted-foreground";
}

/**
 * One number, compactly.
 *
 * Title and icon share the first line and the value sits under it, so a tile is about
 * two thirds the height of the header/content card this replaced. On a dashboard showing
 * twenty-odd figures across six groups, that is the difference between scrolling to find
 * a number and seeing it.
 *
 * When `to` is set the whole tile is the link — a small arrow marks it, and the hover and
 * focus states come from the anchor rather than from a nested button, so keyboard users
 * get the same affordance as pointer users.
 */
export function StatCard({
  title,
  value,
  description,
  icon: Icon,
  to,
  change,
  iconClassName,
  className
}: StatCardProps) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            typography.textXsMuted,
            "truncate font-medium uppercase tracking-wide"
          )}
        >
          {title}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <Icon className={cn("size-4 text-muted-foreground", iconClassName)} />
          {to && (
            <ArrowUpRight
              aria-hidden="true"
              className="size-3.5 text-muted-foreground/50 transition-colors group-hover:text-foreground"
            />
          )}
        </span>
      </div>

      <div className="mt-1 text-xl font-semibold leading-tight tabular-nums">
        {value}
      </div>

      {change !== undefined && (
        <p className={cn("mt-0.5 text-xs tabular-nums", changeTone(change))}>
          {change > 0 ? "+" : ""}
          {change}% from last period
        </p>
      )}

      {description && (
        <p className={cn(typography.textXsMuted, "mt-0.5 line-clamp-2")}>
          {description}
        </p>
      )}
    </>
  );

  if (!to) {
    return <Card className={cn("gap-0 p-3", className)}>{body}</Card>;
  }

  return (
    <Card
      className={cn(
        "group gap-0 p-0 transition-colors hover:border-border",
        "focus-within:ring-2 focus-within:ring-ring/50",
        className
      )}
    >
      <Link
        to={to}
        className="block h-full rounded-[inherit] p-3 outline-none"
        aria-label={`${title}: ${value}`}
      >
        {body}
      </Link>
    </Card>
  );
}
